import React, { useState, useEffect } from 'react';
import { Flex } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../../services/responsiveUtils';

import WizardHeader from '../GenericWizard/Header/WizardHeader.component';
import WizardNavigation from '../GenericWizard/Navigation/WizardNavigation.component';
import { PhoneStep, OtpStep } from './Steps';

export interface TelegramWizardProps {
  phone: string;
  otpCode: string;
  password: string;
  loading: boolean;
  error: string | null;
  step: 'phone' | 'otp';
  accountUuid: string | null;
  isSubmitting: boolean;
  pageTitle: string;
  submitButtonText: string;

  onPhoneChange: (phone: string) => void;
  onOtpChange: (code: string) => void;
  onPasswordChange: (password: string) => void;
  onCreateAccount: () => Promise<void>;
  onStartAuth: () => Promise<void>;
  onSubmit: () => void;
  onClearError: () => void;
  onBackClick?: () => void;
}

const TelegramWizard: React.FC<TelegramWizardProps> = ({
  phone,
  otpCode,
  password,
  loading,
  error,
  step,
  accountUuid,
  isSubmitting,
  pageTitle,
  submitButtonText,
  onPhoneChange,
  onOtpChange,
  onPasswordChange,
  onCreateAccount,
  onStartAuth,
  onSubmit,
  onClearError,
  onBackClick
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [currentStep, setCurrentStep] = useState<number>(0);

  useEffect(() => {
    setCurrentStep(step === 'phone' ? 0 : 1);
  }, [step]);

  const goBack = () => {
    if (onBackClick) {
      onBackClick();
    } else {
      navigate('/telegram_accounts');
    }
  };

  const shouldDisableNext = (): boolean => {
    if (currentStep === 0) {
      return !phone.trim() || loading;
    }
    return !otpCode.trim() || loading;
  };

  const handleNext = async () => {
    if (currentStep === 0) {
      await onCreateAccount();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const steps = [
    {
      title: t('telegram-wizard-phone-title', { ns: 'standard' }),
      description: t('telegram-wizard-phone-description', { ns: 'standard' })
    },
    {
      title: t('telegram-wizard-otp-title', { ns: 'standard' }),
      description: t('telegram-wizard-otp-description', { ns: 'standard' })
    }
  ];

  const getCurrentStepComponent = () => {
    if (currentStep === 0) {
      return (
        <PhoneStep
          phone={phone}
          loading={loading}
          error={error}
          onPhoneChange={onPhoneChange}
          onClearError={onClearError}
        />
      );
    }
    
    return (
      <OtpStep
        phone={phone}
        otpCode={otpCode}
        password={password}
        loading={loading}
        error={error}
        onOtpChange={onOtpChange}
        onPasswordChange={onPasswordChange}
        onClearError={onClearError}
      />
    );
  };

  return (
    <Flex vertical gap="large" style={{ width: '100%' }}>
      <WizardHeader
        pageTitle={pageTitle}
        onBack={goBack}
        backButtonText={t('back', { ns: 'buttons' })}
        steps={steps}
        currentStep={currentStep}
        isMobile={isMobile}
      />

      {getCurrentStepComponent()}

      <WizardNavigation
        currentStep={currentStep}
        stepsCount={2}
        isSubmitting={isSubmitting}
        submitButtonText={submitButtonText}
        backButtonText={t('back', { ns: 'buttons' })}
        nextButtonText={t('send-code', { ns: 'buttons' })}
        onNext={handleNext}
        onPrev={handlePrev}
        onSubmit={onSubmit}
        shouldDisableNext={shouldDisableNext}
      />
    </Flex>
  );
};

export default TelegramWizard;
